import { useEffect } from "react";

// ─── PALETTE ───
const C = {
  night: "#0D1117", deep: "#161B22", surface: "#1C2128",
  border: "#30363D", muted: "#8B949E", text: "#C9D1D9",
  bright: "#E6EDF3", accent: "#D4A574",
  accentGlow: "rgba(212,165,116,0.15)", pulse: "#4A9EBF",
};

const Section = ({ children, style = {} }) => (
  <section style={{ padding: "32px 24px", maxWidth: 680, margin: "0 auto", ...style }}>{children}</section>
);

const Divider = () => (
  <div style={{ width: 60, height: 1, background: C.border, margin: "0 auto" }} />
);

const Heading = ({ children }) => (
  <h2 style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: "clamp(22px, 3.5vw, 30px)", color: C.bright,
    lineHeight: 1.3, marginBottom: 20,
  }}>{children}</h2>
);

const Body = ({ children, style = {} }) => (
  <p style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: 16, lineHeight: 1.8, color: C.text, marginBottom: 18, ...style,
  }}>{children}</p>
);

const Accent = ({ children }) => (
  <span style={{ color: C.accent }}>{children}</span>
);

const Q = ({ children }) => (
  <p style={{
    fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600,
    color: C.bright, letterSpacing: 0.3, marginBottom: 12, marginTop: 36,
  }}>{children}</p>
);

const A = ({ children }) => (
  <p style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: 15, lineHeight: 1.8, color: C.text, marginBottom: 4,
    paddingLeft: 20, borderLeft: `2px solid ${C.border}`,
  }}>{children}</p>
);

const Bullet = ({ children }) => (
  <li style={{
    fontFamily: "'Libre Baskerville', Georgia, serif",
    fontSize: 15, lineHeight: 1.8, color: C.text, marginBottom: 10,
    paddingLeft: 8,
  }}>{children}</li>
);

export default function PopuliBuild() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const goHome = (e) => {
    e.preventDefault();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <div style={{ background: C.night, minHeight: "100vh", color: C.text }}>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${C.accent}44; color: ${C.bright}; }
        ul, ol { padding-left: 20px; }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <header style={{
        minHeight: "50vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        position: "relative", padding: "80px 24px 40px",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(${C.border}15 1px, transparent 1px), linear-gradient(90deg, ${C.border}15 1px, transparent 1px)`,
          backgroundSize: "60px 60px", opacity: 0.3,
        }} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", animation: "fadeInUp 1.2s ease-out" }}>
          <a href="/" onClick={goHome} style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
            letterSpacing: 3, textTransform: "uppercase", color: C.muted,
            textDecoration: "none", display: "block", marginBottom: 32,
          }}>← The Populi Project</a>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
            letterSpacing: 3, textTransform: "uppercase", color: C.pulse,
            marginBottom: 20,
          }}>Contributor Brief</p>
          <h1 style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700,
            color: C.bright, lineHeight: 1.2, marginBottom: 16,
          }}>A Platform for<br /><span style={{ color: C.accent }}>Verified Public Opinion</span></h1>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: "clamp(15px, 2vw, 18px)", fontStyle: "italic",
            color: C.muted, maxWidth: 500, margin: "0 auto",
          }}>An early-stage civic technology project.<br />This document is for people who might want to help build it.</p>
        </div>
      </header>

      <Divider />

      {/* ═══ THE PROBLEM ═══ */}
      <Section>
        <Heading>The Problem</Heading>
        <Body>
          In 2003, global polling showed majorities in nearly every country &mdash; including the US &mdash; opposed or deeply uncertain about the invasion of Iraq. Millions marched. It happened anyway. For over two decades, 80%+ of Americans across party lines have supported Medicare negotiating drug prices. It took twenty years to partially pass. In election after election, both parties nominate candidates the center of their own base is lukewarm about &mdash; because primaries reward the loudest, not the most representative.
        </Body>
        <Body style={{ borderLeft: `2px solid ${C.accent}`, paddingLeft: 24 }}>
          The consensus existed every time. It just had no infrastructure to make it undeniable. That&rsquo;s the only thing missing. Not opinions. Not agreement. <Accent>The tool to surface it.</Accent>
        </Body>
      </Section>

      <Divider />

      {/* ═══ THE IDEA ═══ */}
      <Section>
        <Heading>The Idea</Heading>
        <Body>
          Populi is not a social platform. It&rsquo;s not a movement. It&rsquo;s civic infrastructure &mdash; a platform where verified people weigh in on the issues that matter, and the world sees what people actually think. One verified person. One voice. Community-surfaced questions. Living data explorable by region, age, and time. A methodology transparent enough to audit.
        </Body>
        <Body style={{ borderLeft: `2px solid ${C.accent}`, paddingLeft: 24 }}>
          Not just the country. The world. Because the world is probably more aligned than the loudest voices &mdash; on any given issue, in any given moment &mdash; would have us believe. <Accent>Populi is the instrument to prove it.</Accent>
        </Body>
      </Section>

      <Divider />

      {/* ═══ WHERE WE ARE ═══ */}
      <Section>
        <Heading>Where We Are</Heading>
        <Body>
          There&rsquo;s a vision, a manifesto, and a working prototype at populi.one. The prototype is intentionally conceptual &mdash; a 3D globe, example questions, simulated results. It&rsquo;s meant to convey the idea, not the product. The real product could be far better. That&rsquo;s part of what we&rsquo;re here to figure out.
        </Body>
        <Body>
          What doesn&rsquo;t exist yet: a full team, a locked legal structure, or a production build. Those decisions deserve the right people in the room. That&rsquo;s what this document is for.
        </Body>
      </Section>

      <Divider />

      {/* ═══ FAQ ═══ */}
      <Section>
        <Heading>Honest Answers to the Hard Questions</Heading>

        <Q>Verification seems like the hardest problem. How are you solving it?</Q>
        <A>It is the hardest problem. We start with phone verification &mdash; lightweight enough to scale, meaningful enough to filter bots. But that&rsquo;s not the end state. We&rsquo;re exploring peer-based models: invite webs, community vouching, lightweight video check-ins where users verify each other. The goal is to raise the bar progressively without killing participation. We don&rsquo;t have this fully solved. We&rsquo;re looking for people who&rsquo;ve thought hard about it.</A>

        <Q>What draws people to use this? What&rsquo;s the incentive?</Q>
        <A>The incentive is curiosity about the answer. The moment you submit your response, you see where you stand relative to everyone else &mdash; your city, your age group, your country, the world. That reveal is the hook. It&rsquo;s the same reason people can&rsquo;t stop reading poll results, except now you&rsquo;re in the data.</A>
        <A style={{ marginTop: 12 }}>Underneath that is something more durable: the desire to be counted. Most people have never had a formal mechanism to register their opinion on things that actually affect their life. Populi gives them that. It&rsquo;s not entertainment &mdash; it&rsquo;s civic dignity.</A>
        <A style={{ marginTop: 12 }}>And then there&rsquo;s the natural social layer. &ldquo;Did you see what people your age think about this?&rdquo; is a share moment that doesn&rsquo;t require followers or likes. The data itself becomes the content.</A>
        <A style={{ marginTop: 12 }}>One important note: Populi doesn&rsquo;t need to be used every day to matter. It needs to be used when it matters &mdash; when a question feels urgent, when an election approaches, when a debate is heating up. That&rsquo;s a different engagement model than social media, and it shouldn&rsquo;t try to be.</A>

        <Q>Nonprofit or for-profit? How does this stay independent?</Q>
        <A>For-profit leaning &mdash; nonprofits introduce operational complexity that slows early momentum. But legal structure is a means, not an end. The real question is: how do you lock in a mission so it survives ownership changes or investor pressure? We&rsquo;re exploring benefit corporation structures, open bylaws, and community ownership mechanisms. This is an open decision. We want input before we make it.</A>

        <Q>How does it make money? Will you sell user data?</Q>
        <A>Data could be part of the model &mdash; but only in a framework where users have meaningful control over what&rsquo;s shared. The platform&rsquo;s value is aggregate insight, not individual surveillance. We&rsquo;re also open to institutional licensing and mission-aligned donations. What we won&rsquo;t do: optimize for engagement over honesty, or let revenue distort the data.</A>

        <Q>Who&rsquo;s behind this?</Q>
        <A>A founder operating under the name Civis &mdash; Latin for citizen. The platform is called Populi &mdash; Latin for of the people. Both names are intentional. Both are also open to change &mdash; the community will have a say. The founder is anonymous for now by design: the idea should stand on its own before it stands on a personality. There is no full team yet. That&rsquo;s what this document is for.</A>

        <Q>How can I get involved?</Q>
        <A>We need people who want to do things, not just advise on them. Identity and verification, platform governance, product, engineering, research, legal structure for mission-driven companies. A community space is coming soon. For now, the conversation starts here.</A>

        <Q>Isn&rsquo;t this just another civic tech project that won&rsquo;t get traction?</Q>
        <A>Most civic tech is advocacy wearing a product mask. Populi is different: it&rsquo;s infrastructure, not a point of view. The goal isn&rsquo;t to move people &mdash; it&rsquo;s to count them. To call the roll. Think about how many elections, primaries, policy debates, and decisions about war have been made in the name of &lsquo;the people&rsquo; &mdash; with no reliable way to know what the people actually thought. Populi is the missing instrument. Scale is the whole point: a thousand voices are interesting. A hundred million are impossible to ignore.</A>
      </Section>

      <Divider />

      {/* ═══ WHY WE NEED YOU ═══ */}
      <Section>
        <Heading>Why We Need You</Heading>
        <Body>
          I&rsquo;ve spent nearly two decades wishing this existed. Watching elections get called for the wrong reasons. Watching wars get authorized without anyone asking the public. Watching consensus get drowned out by noise. The tools to build this have finally caught up to the idea.
        </Body>
        <Body>
          But I can&rsquo;t build it alone &mdash; and I shouldn&rsquo;t. The decisions ahead are too important to be made by one person. Verification. Governance. Legal structure. Product. These need the right people in the room. That&rsquo;s you.
        </Body>
        <Body style={{ borderLeft: `2px solid ${C.accent}`, paddingLeft: 24, fontStyle: "italic", color: C.bright }}>
          You don&rsquo;t have to agree with every call we&rsquo;ve made. You just have to believe the problem is real and worth solving. We do &mdash; and we need your help getting it right.
        </Body>
      </Section>

      <Divider />

      {/* ═══ WHO WE'RE LOOKING FOR ═══ */}
      <Section>
        <Heading>Who We&rsquo;re Looking For</Heading>
        <Body>People comfortable with ambiguity, energized by hard problems, honest enough to push back. Specifically:</Body>
        <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
          <Bullet><Accent>Builders</Accent> who&rsquo;ve worked on identity, verification, or trust systems &mdash; and know where they break</Bullet>
          <Bullet><Accent>Researchers</Accent> in political science, public opinion, or social behavior</Bullet>
          <Bullet><Accent>Product and engineering talent</Accent> ready to build something that matters</Bullet>
          <Bullet><Accent>Legal minds</Accent> who&rsquo;ve thought about mission-lock and benefit corporation structures</Bullet>
          <Bullet><Accent>Survey methodologists and public opinion researchers</Accent> &mdash; people who know how to ask questions without corrupting the answer</Bullet>
          <Bullet><Accent>Journalists and accountability reporters</Accent> &mdash; the people who complete the loop. A Populi signal means nothing if it stays on a screen. Journalists are the ones who hold it up and say: here&rsquo;s what the public actually thinks &mdash; now explain why you voted differently. That pressure is the point.</Bullet>
          <Bullet><Accent>Civic thinkers</Accent> who will challenge our assumptions before the world does</Bullet>
        </ul>
      </Section>

      <Divider />

      {/* ═══ FOOTER ═══ */}
      <Section style={{ textAlign: "center", paddingTop: 48, paddingBottom: 80 }}>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 12,
          color: C.muted, letterSpacing: 2, marginBottom: 8,
        }}>populi.one &nbsp;&middot;&nbsp; April 2026 &nbsp;&middot;&nbsp; The Populi Project</p>
        <a href="/" onClick={goHome} style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
          letterSpacing: 2, textTransform: "uppercase", color: C.muted,
          textDecoration: "none", display: "inline-block", marginTop: 16,
          borderBottom: `1px solid ${C.border}`, paddingBottom: 2,
        }}>← Back to populi.one</a>

        <div style={{ marginTop: 48, borderTop: `1px solid ${C.border}`, paddingTop: 32, maxWidth: 520, margin: "48px auto 0" }}>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: 13, lineHeight: 1.8, color: C.muted,
            fontStyle: "italic", textAlign: "center",
          }}>
            On authorship: I&rsquo;ve carried and carefully considered these ideas for nearly two decades. Claude helped me organize, test, and present them clearly. I mention this because any project built on transparency should model it.
          </p>
        </div>
      </Section>
    </div>
  );
}
